import { getSingleGameData } from '../../../stores/games.stores';
import { IGameSeo, ISingleGame } from '../../../interfaces';
import { toast } from 'react-toastify';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { MainContext } from '../../../context/main.context';
import { getAgeVerification, setAgeVerification, setDocumentData } from '../../../common/tools';
import Modal from '../../modal/Modal';
import HeaderGames from '../header';
import Loader from '../../loader';
import { GAME_IFRAME_ID, STATIC_FILES_PATH } from '../../../common/constants';
import i18n from 'i18next';


export default function SingleGame() {
    const [ data, setData ] = useState<ISingleGame>();
    const { settings } = useContext(MainContext);
    const [ verification, setVerification ] = useState<boolean>(getAgeVerification());
    const { language } = i18n;

    const imageFiles = {
        background: data?.backgroundImageFile,
        backgroundFile: data?.backgroundFile,
        popupFile: data?.popupFile,
        popupSmallFile: data?.popupSmallFile,
        verificationBtnFile: data?.verificationBtnFile,
        verificationSmallBtnFile: data?.verificationSmallBtnFile,
    };

    useEffect(() => {
        updateBackgroundImage();

        window.addEventListener('resize', updateBackgroundImage);

        return () => {
            window.removeEventListener('resize', updateBackgroundImage);
        };
    }, [ imageFiles ]);

    useEffect(() => {
        const projectName: string | null | undefined = settings?.projects?.id;

        if (projectName) {
            _getSingleGameData(projectName);
        }
    }, [ settings ]);

    const getImageSrc = (path?: string) => path ? `url(${STATIC_FILES_PATH}${path}) no-repeat 50% transparent` : '';

    const updateBackgroundImage = useCallback(() => {
        const isTabletOrMobile = window.innerWidth <= 1024;
        const isMobile = window.innerWidth <= 767;

        const getStyle = (filePath: string | undefined, fallbackFile: string | undefined, isMainBlock: boolean = false) => {
            if (isMainBlock) {
                return isTabletOrMobile ? getImageSrc(filePath) : getImageSrc(fallbackFile);
            }

            return isMobile ? getImageSrc(filePath) : getImageSrc(fallbackFile);
        };

        const singleGameDiv = document.getElementById('single-game-id');
        const singleGamePopupDiv = document.getElementById('single-game-popup-id');
        const singleGameVerificationBtnDiv = document.getElementById('single-game-verification-btn');

        if (singleGameDiv) {
            singleGameDiv.style.background = getStyle(imageFiles.backgroundFile, imageFiles.background, true);
        }

        if (singleGamePopupDiv) {
            singleGamePopupDiv.style.background = getStyle(imageFiles.popupSmallFile, imageFiles.popupFile);
        }

        if (singleGameVerificationBtnDiv) {
            singleGameVerificationBtnDiv.style.background = getStyle(imageFiles.verificationSmallBtnFile, imageFiles.verificationBtnFile);
        }
    }, [ imageFiles ]);

    const _getSingleGameData = (projectName: string) => {
        getSingleGameData(projectName).then(res => {
            const singleGameData: ISingleGame = res?.data?.settings;
            const seoData: IGameSeo = res?.data?.seoData;

            if (seoData) {
                setDocumentData(seoData);
            }

            if (singleGameData) {
                setData(singleGameData);
                return;
            }

            toast.error('GET_SINGLE_GAME_DATA_ERROR');
            console.error('GET_SINGLE_GAME_DATA_ERROR', { res });
        }).catch(err => {
            console.error('GET_SINGLE_GAME_DATA_ERROR', err?.response?.data || err);
            toast.error('GET_SINGLE_GAME_DATA_ERROR');
        });
    }

    const _onConfirm = () => {
        setAgeVerification('y');
        setVerification(true);
    }

    const _renderVerificationModal = () => {
        return (
            <div id="single-game-popup-id" className="single-game-modal">
                <div className="single-game-modal-title">
                    Age verification
                </div>
                <div className="single-game-modal-description">
                    This game contains age-restricted materials including nudity and explicit depictions of sexual
                    activity.
                    <br/>
                    By continuing you confirm that you are over 18.
                </div>
                <div id="single-game-verification-btn" className="single-game-modal-btn" onClick={_onConfirm}>
                    <div>
                        I am over 18 years old
                    </div>
                </div>
                <div className="single-game-modal-close" onClick={() => window.location.href = "https://www.google.com"}>
                    No, I am under 18 years old
                </div>
            </div>
        );
    }

    const _renderIframe = () => {
        const iframeSrc = data?.iframeSrc;

        if (!iframeSrc || !verification) {
            return null;
        }

        const iframeUrl = new URL(iframeSrc);
        iframeUrl.searchParams.set('language', language);

        return (
            <div className="single-game-wrapper">
                <div className="single-game-container">
                    <iframe src={iframeUrl.toString()} id={GAME_IFRAME_ID} className="single-game-container-frame"/>
                </div>
            </div>
        );
    }

    const _navigate = (fileName: string): void => {
        window.open(`${window.location.origin}/docs/${fileName}.html`, '_blank');
    }

    const _renderFooter = () => {
        return (
            <div className="single-game-footer">
                <p>
                    <span
                        className="link"
                        onClick={() => {
                            window.open(`https://discord.com/channels/1225801090319122472/1258374710207578182`, '_blank');
                        }}
                    >
                        Customer Support
                    </span>
                    {" | "}
                    <span
                        className="link"
                        onClick={() => _navigate('PlayflockLimitedTermsOfUse')}
                    >
                        Terms of use
                    </span>
                    {" | "}
                    <span
                        className="link"
                        onClick={() => _navigate('EnPlayflockLimitedPrivacyPolicy')}
                    >
                        Privacy Policy
                    </span>
                    {" | "}
                    <span
                        className="link"
                        onClick={() => _navigate('2257')}
                    >
                        2257 Exempt
                    </span>
                    {" | "}
                    <span
                        className="link"
                        onClick={() => _navigate('EnComplainPage')}
                    >
                        Complaints & Content Removal
                    </span>
                </p>
            </div>
        );
    }

    const _renderModal = () => {
        if (verification) {
            return null;
        }

        return (
            <Modal hideCloseButton open>{ _renderVerificationModal() }</Modal>
        );
    }

    if (!data) {
        return <Loader/>;
    }

    return (
        <div id="single-game-id" className={`single-game ${!verification ? 'single-game-not-verification' : ''}`}>
            <HeaderGames isGame={false} projectData={data}/>
            { _renderIframe() }
            { _renderFooter() }
            { _renderModal() }
        </div>
    );
}